import { useEffect, useState } from 'react';
import { Building2, Lock, Users, X } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { cn } from '@/lib/utils';
import { supabase } from '@/lib/supabase';
import { type AccessPrincipal, type AppSummary, loadAccessRoster, loadAppAccess, principalKey, saveAppAccess } from '@/lib/app-access';
import type { ToolAppVisibility } from '@shared/types/tool-apps';

const OPTIONS: Array<{ value: ToolAppVisibility; title: string; description: string; icon: typeof Lock }> = [
  { value: 'private', title: 'Juste moi', description: 'Vous et les managers.', icon: Lock },
  { value: 'restricted', title: 'Des équipes ou des personnes', description: 'Celles que vous choisissez.', icon: Users },
  { value: 'org', title: 'Toute l’entreprise', description: 'Chaque membre peut l’ouvrir.', icon: Building2 },
];

interface AppAccessDialogProps {
  app: AppSummary;
  onClose: () => void;
  onSaved: (visibility: ToolAppVisibility) => void;
}

/** Who can open an app: just me, chosen teams or people, or the whole tenant. */
export const AppAccessDialog = ({ app, onClose, onSaved }: AppAccessDialogProps) => {
  const [visibility, setVisibility] = useState<ToolAppVisibility>(app.visibility);
  const [selected, setSelected] = useState<string[]>([]);
  const [roster, setRoster] = useState<AccessPrincipal[]>([]);
  const [canManage, setCanManage] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    void (async () => {
      try {
        const { data } = await supabase.auth.getUser();
        const [access, principals] = await Promise.all([loadAppAccess(app.id), loadAccessRoster(data.user?.id ?? '')]);
        if (!active) return;
        setCanManage(access.canManage);
        setSelected(access.selected);
        setRoster(principals);
      } catch (cause) {
        if (active) setError(cause instanceof Error ? cause.message : 'Impossible de charger les accès.');
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [app.id]);

  const toggle = (key: string) => setSelected((current) => current.includes(key) ? current.filter((item) => item !== key) : [...current, key]);
  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      await saveAppAccess(app.id, visibility, selected);
      onSaved(visibility);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'L’accès n’a pas pu être enregistré.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-domino-ink/30 p-0 backdrop-blur-sm sm:items-center sm:p-4">
      <div role="dialog" aria-modal="true" aria-labelledby="app-access-title"
        className="max-h-[94vh] w-full max-w-xl overflow-y-auto rounded-t-[24px] border border-domino-cardBorder bg-domino-white shadow-2xl sm:rounded-[24px]">
        <div className="flex items-start justify-between gap-4 border-b border-domino-cardBorder px-5 py-4 sm:px-6">
          <div className="min-w-0">
            <h2 id="app-access-title" className="text-lg font-semibold tracking-tight text-domino-ink">Qui peut ouvrir « {app.title} » ?</h2>
            <p className="mt-1 text-sm text-domino-inkSoft">Partager l’application ne change pas les droits sur le CRM.</p>
          </div>
          <button type="button" onClick={onClose} aria-label="Fermer"
            className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-domino-inkSoft hover:bg-domino-muted hover:text-domino-ink focus:outline-none focus-visible:ring-2 focus-visible:ring-domino-prune">
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="space-y-5 px-5 py-5 sm:px-6">
          {loading ? <p role="status" className="text-sm text-domino-inkSoft">Chargement des accès…</p> : (
            <>
              {!canManage && <p className="rounded-xl bg-domino-muted p-3 text-sm text-domino-inkSoft">Seuls le créateur de l’application et les managers règlent son accès.</p>}
              <div role="radiogroup" aria-label="Accès" className="grid gap-2 sm:grid-cols-3">
                {OPTIONS.map((option) => {
                  const Icon = option.icon;
                  const checked = visibility === option.value;
                  return (
                    <button key={option.value} type="button" role="radio" aria-checked={checked} disabled={!canManage}
                      onClick={() => setVisibility(option.value)}
                      className={cn('rounded-2xl border p-3 text-left transition disabled:opacity-60',
                        checked ? 'border-domino-aubergine bg-domino-lilac' : 'border-domino-cardBorder hover:border-domino-aubergine')}>
                      <Icon className={cn('h-4 w-4', checked ? 'text-domino-aubergine' : 'text-domino-inkSoft')} aria-hidden="true" />
                      <p className="mt-2 text-sm font-semibold text-domino-ink">{option.title}</p>
                      <p className="mt-0.5 text-xs leading-5 text-domino-inkSoft">{option.description}</p>
                    </button>
                  );
                })}
              </div>
              {visibility === 'restricted' && (
                <fieldset disabled={!canManage}>
                  <legend className="text-sm font-semibold text-domino-ink">Équipes et personnes</legend>
                  {roster.length === 0 ? <p className="mt-2 text-sm text-domino-inkSoft">Aucune autre personne ni équipe.</p> : (
                    <ul className="mt-2 max-h-64 space-y-1 overflow-y-auto">
                      {roster.map((principal) => {
                        const key = principalKey(principal);
                        return (
                          <li key={key}>
                            <label className="flex cursor-pointer items-center gap-3 rounded-xl px-2 py-1.5 hover:bg-domino-muted">
                              <input type="checkbox" checked={selected.includes(key)} onChange={() => toggle(key)} className="h-4 w-4 accent-domino-aubergine" />
                              <span className="min-w-0 flex-1 text-sm text-domino-ink">{principal.label}</span>
                              <span className="text-xs text-domino-inkSoft">{principal.detail}</span>
                            </label>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </fieldset>
              )}
              {error && <p role="alert" className="text-sm text-[var(--domino-critical)]">{error}</p>}
            </>
          )}
        </div>
        <div className="flex justify-end gap-3 border-t border-domino-cardBorder px-5 py-4 sm:px-6">
          <Button variant="secondary" onClick={onClose}>Fermer</Button>
          {canManage && <Button onClick={() => void save()} disabled={saving || loading}>{saving ? 'Enregistrement…' : 'Enregistrer'}</Button>}
        </div>
      </div>
    </div>
  );
};
